import React from 'react';
import { useMutation, useQuery } from '@apollo/client';
import axios from 'axios';
import { 
  UPDATE_USER_ROLE, 
  UPDATE_USER_PASSWORD,
  UPDATE_USER_AREAS,
  REFRESH_TOKEN
} from '../graphql/mutations';
import { 
  GET_ACTIVE_TEAMS,
  GET_TEAMS,
  UPDATE_USER_TEAM,
  GET_USER_TEAMS,
  ADD_USER_TO_TEAM,
  REMOVE_USER_FROM_TEAM,
  SET_USER_PRIMARY_TEAM
} from '../graphql/teams';
import { GET_TEAM_MEMBER_DIRECTORY_USERS } from '../graphql/queries';
import {
  GET_USER_NOTIFICATION_PREFERENCES,
  UPDATE_USER_NOTIFICATION_PREFERENCE
} from '../graphql/notifications';
import { toast } from 'react-hot-toast';
import { API_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { formatSalary, validateSalary } from './team-members/constants';
import { compressImage, validateImageFile } from '../utils/image-utils';
import SafeAvatar from './SafeAvatar';

const notificationTypeLabels = {
  SCHEDULE_UPDATE: 'Schedule Updates',
  JOB_DATE_CHANGE: 'Job Date Changes',
  JOB_CANCELLATION: 'Job Cancellations',
  READY_FOR_INSPECTION: 'Ready for Inspection',
  INSPECTION_REPORT: 'Inspection Reports',
  WORK_ORDER: 'Work Orders',
  WORK_ORDER_ASSIGNMENT: 'Work Order Assignments',
  WORK_ORDER_COMPLETED: 'Work Order Completed',
  ESCALATED_WORK_ORDER: 'Escalated Work Orders',
  WORK_ORDER_COMMENT_CALL_CENTER: 'Work Order Comment (Reservation Team)',
  LOST_ITEM: 'Lost Items',
  USER_INVITATION: 'User Invitations',
  GUEST_ORDERS: 'Guest Orders',
};

// Define roles enum to match backend exactly
const UserRole = {
  ADMIN: 'ADMIN',
  GUEST_APP_MANAGER: 'GUEST_APP_MANAGER',
  MAINTENANCE: 'MAINTENANCE',
  HOUSEKEEPER: 'HOUSEKEEPER',
  HOUSEKEEPER_MANAGER: 'HOUSEKEEPER_MANAGER',
  INSPECTOR: 'INSPECTOR',
  PROPERTY_MANAGER: 'PROPERTY_MANAGER',
  CALL_CENTER: 'CALL_CENTER'
};

// Add LocationPreference enum
const LocationPreference = {
  KIAWAH: 'KIAWAH',
  ISLE_OF_PALMS: 'ISLE_OF_PALMS',
  ISLE_OF_PALMS_WILD_DUNES: 'ISLE_OF_PALMS_WILD_DUNES',
  SEABROOK_ISLAND: 'SEABROOK_ISLAND'
};

// Add available areas array with combined options only
const AVAILABLE_AREAS = [
  { value: 'KIAWAH_SEABROOK', label: 'Kiawah & Seabrook Island' },
  { value: 'ISLE_OF_PALMS_COMBINED', label: 'Isle of Palms & Wild Dunes' }
];

// Add available roles array
const AVAILABLE_ROLES = [
  { value: UserRole.ADMIN, label: 'Admin' },
  { value: UserRole.GUEST_APP_MANAGER, label: 'Guest App Manager' },
  { value: UserRole.MAINTENANCE, label: 'Maintenance' },
  { value: UserRole.HOUSEKEEPER, label: 'Housekeeper' },
  { value: UserRole.HOUSEKEEPER_MANAGER, label: 'Housekeeper Manager' },
  { value: UserRole.INSPECTOR, label: 'Inspector' },
  { value: UserRole.PROPERTY_MANAGER, label: 'Property Manager' },
  { value: UserRole.CALL_CENTER, label: 'Call Center' }
];

export default function EditTeamMemberModal({ 
  isOpen, 
  onClose, 
  selectedUser, 
  onUpdate, 
  onDeactivate 
}) {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === UserRole.ADMIN;
  const [formData, setFormData] = React.useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    password: '',
    role: UserRole.HOUSEKEEPER,
    areas: [],
    teamId: null, // Legacy single team (for backward compatibility)
    isContractor: false,
    salary: '',
    paidTimeOffDays: '0',
    requiresTimeClock: false,
    timeClockHourlyRate: '',
    showAllDepartmentsByDefault: false,
    receivesHousekeepingNotifications: false,
    emailNotifications: true,
    smsNotifications: true,
    notificationEmailOverride: '',
    avatarUrl: ''
  });
  
  // New multi-team state
  const [userTeams, setUserTeams] = React.useState([]);
  const [selectedTeamToAdd, setSelectedTeamToAdd] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = React.useState(false);
  const [avatarFile, setAvatarFile] = React.useState(null);
  const [imagePreview, setImagePreview] = React.useState(null);
  const [savingNotificationKey, setSavingNotificationKey] = React.useState('');

  const [refreshToken] = useMutation(REFRESH_TOKEN);
  const [updateUserRole] = useMutation(UPDATE_USER_ROLE);
  const [updateUserPassword] = useMutation(UPDATE_USER_PASSWORD);
  const [updateUserAreas] = useMutation(UPDATE_USER_AREAS);
  const [updateUserTeam] = useMutation(UPDATE_USER_TEAM);
  
  const teamAssignmentRefetchQueries = [
    { query: GET_USER_TEAMS, variables: { userId: parseInt(selectedUser?.id, 10) } },
    { query: GET_TEAM_MEMBER_DIRECTORY_USERS },
    { query: GET_TEAMS },
  ];
  const [addUserToTeam] = useMutation(ADD_USER_TO_TEAM, {
    refetchQueries: teamAssignmentRefetchQueries,
  });
  const [removeUserFromTeam] = useMutation(REMOVE_USER_FROM_TEAM, {
    refetchQueries: teamAssignmentRefetchQueries,
  });
  const [setUserPrimaryTeam] = useMutation(SET_USER_PRIMARY_TEAM, {
    refetchQueries: teamAssignmentRefetchQueries,
  });
  const [updateUserNotificationPreference] = useMutation(UPDATE_USER_NOTIFICATION_PREFERENCE);

  const { data: teamsData } = useQuery(GET_ACTIVE_TEAMS, {
    fetchPolicy: 'cache-first'
  });

  const parsedUserId = Number.parseInt(selectedUser?.id, 10);
  const { data: userTeamsData, refetch: refetchUserTeams } = useQuery(GET_USER_TEAMS, {
    variables: { userId: parsedUserId },
    skip: !isOpen || !Number.isFinite(parsedUserId),
    fetchPolicy: 'network-only',
  });

  const {
    data: notificationPreferencesData,
    loading: notificationPreferencesLoading,
    error: notificationPreferencesError,
    refetch: refetchNotificationPreferences
  } = useQuery(GET_USER_NOTIFICATION_PREFERENCES, {
    variables: { userId: selectedUser?.id },
    skip: !isAdmin || !selectedUser?.id || !isOpen,
    fetchPolicy: 'network-only'
  });

  const notificationPreferences = notificationPreferencesData?.userNotificationPreferences || [];

  React.useEffect(() => {
    if (selectedUser) {
      setFormData({
        firstName: selectedUser.firstName || '',
        lastName: selectedUser.lastName || '',
        email: selectedUser.email,
        phoneNumber: selectedUser.phoneNumber || '',
        password: '',
        role: selectedUser.role,
        areas: selectedUser.areas || [],
        teamId: selectedUser.teamId || null,
        isContractor: selectedUser.isContractor || false,
        salary: selectedUser.salary ? selectedUser.salary.toString() : '',
        paidTimeOffDays: String(selectedUser.paidTimeOffDays ?? 0),
        requiresTimeClock: selectedUser.requiresTimeClock || false,
        timeClockHourlyRate: selectedUser.timeClockHourlyRate ? selectedUser.timeClockHourlyRate.toString() : '',
        showAllDepartmentsByDefault: selectedUser.showAllDepartmentsByDefault ?? false,
        receivesHousekeepingNotifications: selectedUser.receivesHousekeepingNotifications ?? false,
        emailNotifications: selectedUser.emailNotifications ?? true,
        smsNotifications: selectedUser.smsNotifications ?? true,
        notificationEmailOverride: selectedUser.notificationEmailOverride || '',
        avatarUrl: selectedUser.avatarUrl || ''
      });
      setAvatarFile(null);
      setImagePreview(
        selectedUser.id
          ? `${API_URL}/api/users/avatar-thumbnail/${selectedUser.id}`
          : null
      );
    }
    // Reset delete confirmation state when user changes
    setShowDeleteConfirmation(false);
  }, [selectedUser]);

  // Reset state when modal is closed
  React.useEffect(() => {
    if (!isOpen) {
      setShowDeleteConfirmation(false);
      setError(null);
      setAvatarFile(null);
      setSavingNotificationKey('');
    }
  }, [isOpen]);

  React.useEffect(() => {
    if (!selectedUser) {
      setUserTeams([]);
      return;
    }
    if (Array.isArray(userTeamsData?.userTeams) && userTeamsData.userTeams.length > 0) {
      setUserTeams(userTeamsData.userTeams);
      return;
    }
    if (selectedUser.userTeams?.length) {
      setUserTeams(selectedUser.userTeams);
      return;
    }
    if (selectedUser.team) {
      setUserTeams([{
        id: `legacy-${selectedUser.team.id}`,
        teamId: selectedUser.team.id,
        isPrimary: true,
        team: selectedUser.team,
      }]);
      return;
    }
    if (Array.isArray(userTeamsData?.userTeams)) {
      setUserTeams(userTeamsData.userTeams);
      return;
    }
    setUserTeams([]);
  }, [selectedUser, userTeamsData]);

  const formatPhoneNumber = (phoneNumber) => {
    // Remove all non-numeric characters
    const cleaned = phoneNumber.replace(/\D/g, '');
    
    // Format as (XXX) XXX-XXXX
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0,3)}) ${cleaned.slice(3,6)}-${cleaned.slice(6)}`;
    }
    
    return phoneNumber;
  };

  const validatePhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return true; // Allow empty phone numbers
    const regex = /^(\+?1?[ -]?)?\(?([0-9]{3})\)?[ -]?([0-9]{3})[ -]?([0-9]{4})$/;
    return regex.test(phoneNumber);
  };


  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'phoneNumber') {
      // Only allow numbers, spaces, dashes, and parentheses
      const filtered = value.replace(/[^\d\s\-()]/g, '');
      setFormData(prev => ({
        ...prev,
        [name]: filtered
      }));
    } else if (name === 'paidTimeOffDays') {
      if (value !== '' && !/^\d{0,3}$/.test(value)) return;
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    } else if (name === 'salary' || name === 'timeClockHourlyRate') {
      // Format money inputs
      const formatted = formatSalary(value);
      setFormData(prev => ({
        ...prev,
        [name]: formatted
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  // Multi-team handlers
  const handleAddTeam = async () => {
    if (!selectedTeamToAdd) return;
    
    try {
      setIsLoading(true);
      const isPrimary = userTeams.length === 0; // First team added becomes primary
      
      const { data } = await addUserToTeam({
        variables: {
          userId: parseInt(selectedUser.id),
          teamId: parseInt(selectedTeamToAdd),
          isPrimary
        }
      });
      
      if (data?.addUserToTeam) {
        toast.success('User added to team successfully');
        setSelectedTeamToAdd('');
        // Only refresh the user teams data locally, don't call onUpdate() which closes the modal
        await refetchUserTeams();
        // Don't call onUpdate() here - let the user continue editing
      }
    } catch (error) {
      console.error('Error adding user to team:', error);
      toast.error(error.message || 'Failed to add user to team');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveTeam = async (teamId) => {
    try {
      setIsLoading(true);
      const { data } = await removeUserFromTeam({
        variables: {
          userId: parseInt(selectedUser.id),
          teamId: parseInt(teamId)
        }
      });
      
      if (data?.removeUserFromTeam) {
        toast.success('User removed from team successfully');
        await refetchUserTeams();
        // Don't call onUpdate() here - let the user continue editing
      }
    } catch (error) {
      console.error('Error removing user from team:', error);
      toast.error(error.message || 'Failed to remove user from team');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetPrimaryTeam = async (teamId) => {
    try {
      setIsLoading(true);
      const { data } = await setUserPrimaryTeam({
        variables: {
          userId: parseInt(selectedUser.id),
          teamId: parseInt(teamId)
        }
      });
      
      if (data?.setUserPrimaryTeam) {
        toast.success('Primary team updated successfully');
        await refetchUserTeams();
        // Don't call onUpdate() here - let the user continue editing
      }
    } catch (error) {
      console.error('Error setting primary team:', error);
      toast.error(error.message || 'Failed to set primary team');
    } finally {
      setIsLoading(false);
    }
  };

  // Get available teams (teams the user is not already in)
  const getAvailableTeams = () => {
    if (!teamsData?.activeTeams) return [];
    const userTeamIds = new Set(userTeams.map((userTeam) => String(userTeam.teamId)));
    return teamsData.activeTeams.filter((team) => !userTeamIds.has(String(team.id)));
  };

  const handleAvatarFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const validation = validateImageFile(file);
      if (!validation.isValid) {
        toast.error(validation.error || 'Invalid image file');
        return;
      }
      const compressedFile = await compressImage(file, {
        maxWidth: 400,
        maxHeight: 400,
        quality: 0.85,
        outputFormat: 'image/jpeg'
      });
      setAvatarFile(compressedFile);
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) setImagePreview(e.target.result);
      };
      reader.readAsDataURL(compressedFile);
      setFormData(prev => ({ ...prev, avatarUrl: 'pending-upload' }));
      toast.success('Image selected. Click Update to save.');
    } catch (err) {
      toast.error(err.message || 'Failed to process image');
    }
  };

  const handleUploadAvatar = async () => {
    if (!avatarFile) return null;
    const response = await fetch(`${API_URL}/api/users/get-avatar-upload-url`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        fileName: avatarFile.name,
        fileType: avatarFile.type
      })
    });
    if (!response.ok) {
      throw new Error(`Failed to get upload URL: ${response.status}`);
    }
    const { uploadUrl, imageUrl, headers } = await response.json();
    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      headers,
      body: avatarFile
    });
    if (!uploadResponse.ok) {
      throw new Error('Failed to upload avatar');
    }
    return imageUrl;
  };

  const handleToggleUserAlertPreference = async (preference, field) => {
    if (!isAdmin || !selectedUser?.id) return;
    const nextValue = !preference[field];
    const savingKey = `${preference.notificationType}:${field}`;
    setSavingNotificationKey(savingKey);
    try {
      await updateUserNotificationPreference({
        variables: {
          userId: selectedUser.id,
          notificationType: preference.notificationType,
          emailEnabled: field === 'emailEnabled' ? nextValue : undefined,
          smsEnabled: field === 'smsEnabled' ? nextValue : undefined
        }
      });
      await refetchNotificationPreferences();
      const label = notificationTypeLabels[preference.notificationType]
        || preference.notificationType.replace(/_/g, ' ');
      toast.success(`${label} ${field === 'emailEnabled' ? 'email' : 'SMS'} ${nextValue ? 'enabled' : 'disabled'}`);
    } catch (err) {
      toast.error(err.message || 'Failed to update alert preference');
    } finally {
      setSavingNotificationKey('');
    }
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    setIsLoading(true);
    setError(null);

    try {
      if (!selectedUser) {
        throw new Error('No user selected for update');
      }

      // Validate required fields
      if (!formData.firstName?.trim()) {
        throw new Error('First name is required');
      }

      if (!formData.email?.trim()) {
        throw new Error('Email is required');
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        throw new Error('Please provide a valid email address');
      }

      // Validate phone number format
      if (formData.phoneNumber && !validatePhoneNumber(formData.phoneNumber)) {
        throw new Error('Please provide a valid phone number format (e.g., 843-560-3110, (843) 560-3110, 8435603110)');
      }

      // Validate salary format
      if (formData.salary && !validateSalary(formData.salary)) {
        throw new Error('Please provide a valid salary amount (0-999999.99)');
      }

      if (formData.timeClockHourlyRate && !validateSalary(formData.timeClockHourlyRate)) {
        throw new Error('Please provide a valid time clock hourly rate (0-999999.99)');
      }

      const paidTimeOffDays = Number(formData.paidTimeOffDays);
      if (!Number.isInteger(paidTimeOffDays) || paidTimeOffDays < 0 || paidTimeOffDays > 365) {
        throw new Error('Paid time off days must be a whole number between 0 and 365');
      }

      // Try to refresh the token first
      try {
        await refreshToken();
      } catch (err) {
        console.warn('Failed to refresh token:', err);
        // Continue anyway as the current token might still be valid
      }

      // First update the role if it has changed
      const currentRole = selectedUser.role?.toUpperCase();
      const newRole = formData.role.toUpperCase();
      
      if (currentRole !== newRole) {
        try {
          const { data: roleData } = await updateUserRole({
            variables: {
              userId: parseFloat(selectedUser.id),
              role: newRole
            }
          });
          
          if (!roleData?.updateUserRole) {
            throw new Error('Failed to update role');
          }
        } catch (err) {
          console.error('Error updating role:', err);
          throw new Error(`Failed to update role: ${err.message}`);
        }
      }

      // Then update the profile information if changed
      // Normalize boolean values for comparison
      const currentShowAllDepartments = Boolean(selectedUser.showAllDepartmentsByDefault ?? false);
      const newShowAllDepartments = Boolean(formData.showAllDepartmentsByDefault);
      const currentRequiresTimeClock = Boolean(selectedUser.requiresTimeClock ?? false);
      const newRequiresTimeClock = Boolean(formData.requiresTimeClock);
      const currentTimeClockHourlyRate = selectedUser.timeClockHourlyRate !== null && selectedUser.timeClockHourlyRate !== undefined
        ? parseFloat(selectedUser.timeClockHourlyRate)
        : null;
      const newTimeClockHourlyRate = formData.timeClockHourlyRate ? parseFloat(formData.timeClockHourlyRate) : null;
      const currentReceivesHkNotifications = Boolean(selectedUser.receivesHousekeepingNotifications ?? false);
      const newReceivesHkNotifications = Boolean(formData.receivesHousekeepingNotifications);
      const currentEmailNotifications = selectedUser.emailNotifications ?? true;
      const currentSmsNotifications = selectedUser.smsNotifications ?? true;
      const currentNotificationEmailOverride = selectedUser.notificationEmailOverride || '';
      const newNotificationEmailOverride = (formData.notificationEmailOverride || '').trim();

      if (newNotificationEmailOverride && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newNotificationEmailOverride)) {
        throw new Error('Please provide a valid notification email override address');
      }

      let uploadedAvatarUrl = null;
      if (isAdmin && avatarFile) {
        uploadedAvatarUrl = await handleUploadAvatar();
      }

      const alertOrAvatarChanged = isAdmin && (
        formData.emailNotifications !== currentEmailNotifications ||
        formData.smsNotifications !== currentSmsNotifications ||
        newNotificationEmailOverride !== currentNotificationEmailOverride ||
        Boolean(uploadedAvatarUrl)
      );

      if (formData.firstName !== selectedUser.firstName || 
          (formData.lastName || '') !== (selectedUser.lastName || '') ||
          formData.email !== selectedUser.email ||
          formData.phoneNumber !== selectedUser.phoneNumber ||
          formData.isContractor !== selectedUser.isContractor ||
          (formData.salary ? parseFloat(formData.salary) : null) !== selectedUser.salary ||
          paidTimeOffDays !== (selectedUser.paidTimeOffDays ?? 0) ||
          newShowAllDepartments !== currentShowAllDepartments ||
          newRequiresTimeClock !== currentRequiresTimeClock ||
          newTimeClockHourlyRate !== currentTimeClockHourlyRate ||
          newReceivesHkNotifications !== currentReceivesHkNotifications ||
          alertOrAvatarChanged) {
        
        // Format phone number before submission
        const formattedPhoneNumber = formData.phoneNumber ? formatPhoneNumber(formData.phoneNumber) : '';
        
        // Prepare profile update input
        const profileInput = {
          firstName: formData.firstName.trim(),
          lastName: (formData.lastName || '').trim(),
          email: formData.email.trim(),
          phoneNumber: formattedPhoneNumber,
          isContractor: formData.isContractor,
          salary: formData.salary ? parseFloat(formData.salary) : null,
          paidTimeOffDays,
          requiresTimeClock: newRequiresTimeClock,
          timeClockHourlyRate: newTimeClockHourlyRate,
          showAllDepartmentsByDefault: newShowAllDepartments,
          receivesHousekeepingNotifications: newReceivesHkNotifications
        };

        if (isAdmin) {
          profileInput.emailNotifications = Boolean(formData.emailNotifications);
          profileInput.smsNotifications = Boolean(formData.smsNotifications);
          profileInput.notificationEmailOverride = newNotificationEmailOverride;
          if (uploadedAvatarUrl) {
            profileInput.avatarUrl = uploadedAvatarUrl;
          }
        }
        
        
        try {
          // Use axios for REST API call instead of GraphQL mutation
          const token = localStorage.getItem('access_token');
          const response = await axios.patch(
            `${API_URL}/api/auth/profile/${selectedUser.id}`,
            profileInput,
            {
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`
              }
            }
          );
          
          if (response.data && response.data.success) {
            // Show success message
            toast.success('Profile updated successfully');
            if (uploadedAvatarUrl) {
              setAvatarFile(null);
              setFormData(prev => ({ ...prev, avatarUrl: uploadedAvatarUrl }));
              setImagePreview(`${API_URL}/api/users/avatar-thumbnail/${selectedUser.id}?t=${Date.now()}`);
            }
            
            // Call onUpdate to refresh the parent component
            await onUpdate();
          } else {
            throw new Error('Server returned no success status');
          }
        } catch (err) {
          console.error('Error updating profile:', err);
          // Log detailed error information
          if (err.response) {
            console.error('Response Error:', err.response.data);
            console.error('Status Code:', err.response.status);
          }
          
          let errorMessage = 'Failed to update profile';
          if (err.response?.data?.message) {
            errorMessage = err.response.data.message;
          } else if (err.message) {
            errorMessage = err.message;
          }
          
          throw new Error(`Failed to update profile: ${errorMessage}`);
        }
      }

      // Update areas if changed
      if (JSON.stringify(formData.areas || []) !== JSON.stringify(selectedUser.areas || [])) {
        try {
          // Use axios for REST API call instead of GraphQL mutation
          const token = localStorage.getItem('access_token');
          const response = await axios.patch(
            `${API_URL}/api/auth/profile/${selectedUser.id}/areas`,
            { areas: formData.areas || [] },
            {
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`
              }

            }
          );
          
          if (response.data && response.data.success) {
            // Show success message
            toast.success('Areas updated successfully');
            
            // Call onUpdate to refresh the parent component
            await onUpdate();
          } else {
            throw new Error('Server returned no success status');
          }
        } catch (err) {
          console.error('Error updating areas:', err);
          // Log detailed error information
          if (err.response) {
            console.error('Response Error:', err.response.data);
            console.error('Status Code:', err.response.status);
          }
          
          let errorMessage = 'Failed to update areas';
          if (err.response?.data?.message) {
            errorMessage = err.response.data.message;
          } else if (err.message) {
            errorMessage = err.message;
          }
          
          throw new Error(`Failed to update areas: ${errorMessage}`);
        }
      }

      // Update team assignment if changed
      if (formData.teamId !== selectedUser.teamId) {
        try {
          const { data: teamData } = await updateUserTeam({
            variables: {
              userId: parseFloat(selectedUser.id),
              teamId: formData.teamId ? parseFloat(formData.teamId) : null
            }
          });
          
          if (!teamData?.updateUserTeam) {
            throw new Error('Failed to update team assignment');
          }
          
          // Show success message
          toast.success('Team assignment updated successfully');
          
          // Call onUpdate to refresh the parent component
          await onUpdate();
        } catch (err) {
          console.error('Error updating team assignment:', err);
          let errorMessage = 'Failed to update team assignment';
          if (err.message) {
            errorMessage = err.message;
          }
          throw new Error(errorMessage);
        }
      }

      // Update password if provided
      if (formData.password && formData.password.trim()) {
        try {
          // Validate password length
          if (formData.password.length < 6) {
            throw new Error('Password must be at least 6 characters long');
          }

          const { data: passwordData } = await updateUserPassword({
            variables: {
              userId: parseFloat(selectedUser.id),
              password: formData.password.trim()
            }
          });
          
          
          if (!passwordData?.updatePassword?.success) {
            const errorMessage = passwordData?.updatePassword?.message || 'Failed to update password';
            throw new Error(errorMessage);
          }
          
          // Show success message
          toast.success('Password updated successfully');
          
          // Clear the password field after successful update
          setFormData(prev => ({
            ...prev,
            password: ''
          }));
          
          // Call onUpdate to refresh the parent component
          await onUpdate();
        } catch (err) {
          console.error('Error updating password:', err);
          console.error('GraphQL errors:', err.graphQLErrors);
          console.error('Network error:', err.networkError);
          
          let errorMessage = 'Failed to update password';
          
          // Check for GraphQL errors
          if (err.graphQLErrors && err.graphQLErrors.length > 0) {
            errorMessage = err.graphQLErrors[0].message || errorMessage;
          }
          // Check for network errors
          else if (err.networkError && err.networkError.result && err.networkError.result.errors) {
            errorMessage = err.networkError.result.errors[0].message || errorMessage;
          }
          // Check for general error message
          else if (err.message) {
            errorMessage = err.message;
          }
          
          throw new Error(errorMessage);
        }
      }

      // Close the modal
      onClose();
    } catch (err) {
      console.error('Error updating team member:', err);
      setError(err.message || 'Failed to update team member');
      
      // Show error toast
      toast.error(err.message || 'Failed to update team member');
    } finally {
      setIsLoading(false);
    }
  };

    const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Use axios for REST API call instead of GraphQL mutation
      const token = localStorage.getItem('access_token');
      const response = await axios.delete(
        `${API_URL}/api/users/${selectedUser.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      if (response.data && response.data.success) {
        // Show success message
        toast.success('User deleted successfully');
        
        // Close the modal
        onClose();
        
        // Call onUpdate to refresh the parent component
        await onUpdate();
      } else {
        throw new Error('Server returned no success status');
      }
    } catch (err) {
      console.error('Error deleting user:', err);
      // Log detailed error information
      if (err.response) {
        console.error('Response Error:', err.response.data);
        console.error('Status Code:', err.response.status);
      }
      
      let errorMessage = 'Failed to delete user';
      
      // Check for foreign key constraint violation
      if (err.response?.data?.message && 
          (err.response.data.message.includes('foreign key constraint') || 
           err.response.data.message.includes('FK_') ||
           err.response.data.message.includes('violates'))) {
        errorMessage = `Cannot delete ${selectedUser.firstName} ${selectedUser.lastName} because they have jobs or other records associated with their account. Please deactivate the user instead of deleting them.`;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full">
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">Edit Team Member</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 p-2 text-sm bg-red-50 border border-red-200 text-red-600 rounded">
              {error}
            </div>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                  First Name
                </label>
                <input
                  type="text"
                  name="firstName"
                  id="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full rounded-md border border-gray-500 p-2 shadow-sm focus:border-sky-500 focus:ring-sky-500"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  id="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full rounded-md border border-gray-500 p-2 shadow-sm focus:border-sky-500 focus:ring-sky-500"
                />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password (leave blank to keep unchanged)
                </label>
                <input
                  type="password"
                  name="password"
                  id="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  minLength={6}
                  className="mt-1 block w-full rounded-md border border-gray-500 p-2 shadow-sm focus:border-sky-500 focus:ring-sky-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Work Areas
                </label>
                <div className="space-y-2">
                  {AVAILABLE_AREAS.map(area => {
                    // Check if this combined area is selected
                    let isSelected = false;
                    if (area.value === 'KIAWAH_SEABROOK') {
                      isSelected = formData.areas.includes(LocationPreference.KIAWAH) && 
                                  formData.areas.includes(LocationPreference.SEABROOK_ISLAND);
                    } else if (area.value === 'ISLE_OF_PALMS_COMBINED') {
                      isSelected = formData.areas.includes(LocationPreference.ISLE_OF_PALMS) && 
                                  formData.areas.includes(LocationPreference.ISLE_OF_PALMS_WILD_DUNES);
                    }
                    
                    return (
                      <label key={area.value} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            let newAreas = [...formData.areas];
                            
                            if (area.value === 'KIAWAH_SEABROOK') {
                              if (checked) {
                                newAreas.push(LocationPreference.KIAWAH, LocationPreference.SEABROOK_ISLAND);
                              } else {
                                newAreas = newAreas.filter(a => a !== LocationPreference.KIAWAH && a !== LocationPreference.SEABROOK_ISLAND);
                              }
                            } else if (area.value === 'ISLE_OF_PALMS_COMBINED') {
                              if (checked) {
                                newAreas.push(LocationPreference.ISLE_OF_PALMS, LocationPreference.ISLE_OF_PALMS_WILD_DUNES);
                              } else {
                                newAreas = newAreas.filter(a => a !== LocationPreference.ISLE_OF_PALMS && a !== LocationPreference.ISLE_OF_PALMS_WILD_DUNES);
                              }
                            }
                            
                            // Remove duplicates
                            newAreas = [...new Set(newAreas)];
                            
                            setFormData(prev => ({
                              ...prev,
                              areas: newAreas
                            }));
                          }}
                          className="h-4 w-4 text-sky-600 focus:ring-sky-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">{area.label}</span>
                      </label>
                    );
                  })}
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  Select the work location(s) for this team member. Each option includes multiple areas.
                </p>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                  Last Name
                </label>
                <input
                  type="text"
                  name="lastName"
                  id="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border border-gray-500 p-2 shadow-sm focus:border-sky-500 focus:ring-sky-500"
                />
              </div>
              <div>
                <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700">
                  Phone Number
                </label>
                <input
                  type="text"
                  name="phoneNumber"
                  id="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border border-gray-500 p-2 shadow-sm focus:border-sky-500 focus:ring-sky-500"
                />
              </div>
              <div>
                <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                  Role
                </label>
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border border-gray-500 p-2 shadow-sm focus:border-sky-500 focus:ring-sky-500"
                >
                  {AVAILABLE_ROLES.map(role => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </div>
              {/* Multi-Team Assignment Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Team Assignments
                </label>
                
                {/* Current Teams */}
                {userTeams.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-600 mb-2">Current Teams:</h4>
                    <div className="space-y-2">
                      {userTeams.map((userTeam) => (
                        <div key={userTeam.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                          <div className="flex items-center space-x-3">
                            <div 
                              className="w-4 h-4 rounded-full"
                              style={{ backgroundColor: userTeam.team?.color || '#6B7280' }}
                            ></div>
                            <span className="font-medium text-gray-900">
                              {userTeam.team?.name}
                            </span>
                            {userTeam.isPrimary && (
                              <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                                Primary
                              </span>
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            {!userTeam.isPrimary && (
                              <button
                                type="button"
                                onClick={() => handleSetPrimaryTeam(userTeam.teamId)}
                                disabled={isLoading}
                                className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                              >
                                Set Primary
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleRemoveTeam(userTeam.teamId)}
                              disabled={isLoading}
                              className="text-xs px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Add New Team */}
                <div className="flex space-x-2">
                  <select
                    value={selectedTeamToAdd}
                    onChange={(e) => setSelectedTeamToAdd(e.target.value)}
                    className="flex-1 rounded-md border border-gray-500 p-2 shadow-sm focus:border-sky-500 focus:ring-sky-500"
                    disabled={isLoading || getAvailableTeams().length === 0}
                  >
                    <option value="">Select a team to add...</option>
                    {getAvailableTeams().map(team => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={handleAddTeam}
                    disabled={!selectedTeamToAdd || isLoading || getAvailableTeams().length === 0}
                    className="px-4 py-2 bg-sky-600 text-white rounded-md hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Add
                  </button>
                </div>
                
                {getAvailableTeams().length === 0 && userTeams.length > 0 && (
                  <p className="mt-1 text-xs text-gray-500">
                    User is already a member of all available teams.
                  </p>
                )}
                
                {userTeams.length === 0 && (
                  <p className="mt-1 text-xs text-gray-500">
                    User is not currently assigned to any teams. Add them to a team for organized job assignments.
                  </p>
                )}
                
                <p className="mt-2 text-xs text-gray-500">
                  Users can be members of multiple teams. The primary team is used for default assignments and reporting.
                </p>
              </div>
              
              {/* Salary Field - Only visible to Admin users */}
              {currentUser?.role === UserRole.ADMIN && (
                <div>
                  <label htmlFor="salary" className="block text-sm font-medium text-gray-700">
                    Salary (Weekly Salary)
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">$</span>
                    </div>
                    <input
                      type="text"
                      name="salary"
                      id="salary"
                      value={formData.salary}
                      onChange={handleInputChange}
                      placeholder="0.00"
                      className="block w-full pl-7 pr-3 py-2 rounded-md border border-gray-500 shadow-sm focus:border-sky-500 focus:ring-sky-500"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Enter the weekly salary for this team member (used in reports)
                  </p>
                </div>
              )}

              {currentUser?.role === UserRole.ADMIN && (
                <div>
                  <label htmlFor="paidTimeOffDays" className="block text-sm font-medium text-gray-700">
                    Annual Paid Time Off Days
                  </label>
                  <input
                    type="number"
                    name="paidTimeOffDays"
                    id="paidTimeOffDays"
                    min="0"
                    max="365"
                    step="1"
                    value={formData.paidTimeOffDays}
                    onChange={handleInputChange}
                    className="mt-1 block w-full px-3 py-2 rounded-md border border-gray-500 shadow-sm focus:border-sky-500 focus:ring-sky-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    The team member's annual PTO allowance in whole days.
                  </p>
                </div>
              )}
              
              {/* Contractor Checkbox */}
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="isContractor"
                    checked={formData.isContractor}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-sky-600 focus:ring-sky-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">
                    Contractor
                  </span>
                </label>
                <p className="mt-1 text-xs text-gray-500">
                  Mark this user as a contractor for reporting purposes
                </p>
              </div>

              {/* Time Clock Settings */}
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="requiresTimeClock"
                    checked={formData.requiresTimeClock}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-sky-600 focus:ring-sky-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">
                    Requires Time Clock
                  </span>
                </label>
                <p className="mt-1 text-xs text-gray-500">
                  Include this team member in time-clock punch tracking and payroll hour reporting.
                </p>

                {formData.requiresTimeClock && (
                  <div className="mt-3">
                    <label htmlFor="timeClockHourlyRate" className="block text-sm font-medium text-gray-700">
                      Time Clock Hourly Rate
                    </label>
                    <div className="mt-1 relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">$</span>
                      </div>
                      <input
                        type="text"
                        name="timeClockHourlyRate"
                        id="timeClockHourlyRate"
                        value={formData.timeClockHourlyRate}
                        onChange={handleInputChange}
                        placeholder="0.00"
                        className="block w-full pl-7 pr-3 py-2 rounded-md border border-gray-500 shadow-sm focus:border-sky-500 focus:ring-sky-500"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Show All Departments by Default Checkbox */}
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="showAllDepartmentsByDefault"
                    checked={formData.showAllDepartmentsByDefault}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-sky-600 focus:ring-sky-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">
                    Show All Departments by Default
                  </span>
                </label>
                <p className="mt-1 text-xs text-gray-500">
                  When enabled, this user will see jobs from all departments by default in job filtering
                </p>
              </div>

              {/* Receive Housekeeping Work Order Emails Checkbox */}
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="receivesHousekeepingNotifications"
                    checked={formData.receivesHousekeepingNotifications}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-sky-600 focus:ring-sky-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">
                    Receive Housekeeping Work Order Emails
                  </span>
                </label>
                <p className="mt-1 text-xs text-gray-500">
                  Send this user Housekeeping work order emails in addition to their normal role notifications. Respects the user&apos;s assigned Work Areas.
                </p>
              </div>
            </div>
          </div>

          {isAdmin && (
            <div className="mt-8 pt-6 border-t border-gray-200 space-y-6">
              <div>
                <h3 className="text-sm font-medium text-gray-900">User Settings (Admin)</h3>
                <p className="mt-1 text-sm text-gray-500">
                  View and edit this team member&apos;s alert preferences and avatar. Only administrators can change these.
                </p>
              </div>

              <div className="flex items-center gap-4">
                <SafeAvatar
                  src={imagePreview}
                  alt={`${formData.firstName || 'Team member'} avatar`}
                  className="h-16 w-16 rounded-full object-cover"
                  size={64}
                  lazy={false}
                />
                <div>
                  <label htmlFor="admin-team-member-avatar" className="block text-sm font-medium text-gray-700">
                    Avatar
                  </label>
                  <input
                    id="admin-team-member-avatar"
                    type="file"
                    accept="image/*,.heic,.heif"
                    onChange={handleAvatarFileChange}
                    className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-sky-50 file:text-sky-700 hover:file:bg-sky-100"
                    aria-label="Upload team member avatar"
                    tabIndex={0}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="flex items-start rounded-md border border-gray-200 p-4">
                  <input
                    type="checkbox"
                    name="emailNotifications"
                    checked={Boolean(formData.emailNotifications)}
                    onChange={handleInputChange}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                  />
                  <span className="ml-3">
                    <span className="block text-sm font-medium text-gray-900">Email Notifications</span>
                    <span className="block text-xs text-gray-500">Master switch for all email alerts</span>
                  </span>
                </label>
                <label className="flex items-start rounded-md border border-gray-200 p-4">
                  <input
                    type="checkbox"
                    name="smsNotifications"
                    checked={Boolean(formData.smsNotifications)}
                    onChange={handleInputChange}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                  />
                  <span className="ml-3">
                    <span className="block text-sm font-medium text-gray-900">SMS Notifications</span>
                    <span className="block text-xs text-gray-500">Master switch for all text alerts</span>
                  </span>
                </label>
              </div>

              <div className="rounded-md border border-gray-200 p-4">
                <label htmlFor="admin-notification-email-override" className="block text-sm font-medium text-gray-900">
                  Notification Email Override
                </label>
                <p className="mt-1 text-xs text-gray-500">
                  Route job, work order, and other operational alert emails to a different address.
                  Leave blank to use their account email ({selectedUser?.email}). Password reset and invitation emails always use the account email.
                </p>
                <input
                  type="email"
                  id="admin-notification-email-override"
                  name="notificationEmailOverride"
                  value={formData.notificationEmailOverride || ''}
                  onChange={handleInputChange}
                  className="mt-3 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-sky-500 focus:ring-sky-500"
                  placeholder="alternate@example.com"
                  aria-label="Notification email override"
                  tabIndex={0}
                />
              </div>

              <div>
                <div className="mb-3 flex items-center justify-between">
                  <h4 className="text-sm font-medium text-gray-900">Alerts Enabled for Their Role</h4>
                  {notificationPreferencesLoading && (
                    <span className="text-xs text-gray-500">Loading...</span>
                  )}
                </div>
                {notificationPreferencesError && (
                  <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    {notificationPreferencesError.message}
                  </div>
                )}
                {!notificationPreferencesLoading && !notificationPreferencesError && notificationPreferences.length === 0 && (
                  <div className="rounded-md bg-gray-50 p-4 text-sm text-gray-500">
                    No role-enabled alerts are currently available for this user.
                  </div>
                )}
                {notificationPreferences.length > 0 && (
                  <div className="overflow-hidden rounded-lg border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Alert</th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Email</th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">SMS</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {notificationPreferences.map((preference) => {
                          const emailSavingKey = `${preference.notificationType}:emailEnabled`;
                          const smsSavingKey = `${preference.notificationType}:smsEnabled`;
                          const label = notificationTypeLabels[preference.notificationType]
                            || preference.notificationType.replace(/_/g, ' ');
                          return (
                            <tr key={preference.notificationType}>
                              <td className="px-4 py-4 text-sm font-medium text-gray-900">{label}</td>
                              <td className="px-4 py-4 text-sm text-gray-600">
                                <label className={`inline-flex items-center ${preference.roleEmailEnabled ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                                  <input
                                    type="checkbox"
                                    checked={Boolean(preference.emailEnabled)}
                                    disabled={!preference.roleEmailEnabled || savingNotificationKey === emailSavingKey}
                                    onChange={() => handleToggleUserAlertPreference(preference, 'emailEnabled')}
                                    className="h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                                    aria-label={`${label} email`}
                                  />
                                </label>
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-600">
                                <label className={`inline-flex items-center ${preference.roleSmsEnabled ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                                  <input
                                    type="checkbox"
                                    checked={Boolean(preference.smsEnabled)}
                                    disabled={!preference.roleSmsEnabled || savingNotificationKey === smsSavingKey}
                                    onChange={() => handleToggleUserAlertPreference(preference, 'smsEnabled')}
                                    className="h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                                    aria-label={`${label} SMS`}
                                  />
                                </label>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-sky-600 hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading}
            >
              {isLoading ? 'Updating...' : 'Update'}
            </button>
          </div>
          
          {/* Deactivate User Section - Only shown for active users */}
          {selectedUser?.isActive && (
            <div className="mt-8 pt-6 border-t border-gray-200">
              <h3 className="text-sm font-medium text-gray-900">Deactivate User</h3>
              <p className="mt-1 text-sm text-gray-500">
                Deactivating a user will prevent them from logging in and hide them from the active users list.
              </p>
              <div className="mt-3">
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    onDeactivate(selectedUser);
                  }}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Deactivate User
                </button>
              </div>
            </div>
          )}

          {/* Delete User Section - Only shown for admins */}
          <div className="mt-8 pt-6 border-t border-red-200">
            <h3 className="text-sm font-medium text-red-900">Danger Zone</h3>
            <p className="mt-1 text-sm text-red-600">
              <strong>Warning:</strong> Permanently delete this user and all associated data. This action cannot be undone.
              <br />
              <strong>Note:</strong> Users with existing jobs or records cannot be deleted and should be deactivated instead.
            </p>
            <div className="mt-3">
              {!showDeleteConfirmation ? (
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirmation(true)}
                  className="inline-flex items-center px-3 py-2 border border-red-300 text-sm leading-4 font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete User Permanently
                </button>
              ) : (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">
                        Are you absolutely sure?
                      </h3>
                      <div className="mt-2 text-sm text-red-700">
                        <p>
                          This will permanently delete <strong>{selectedUser?.firstName} {selectedUser?.lastName}</strong> and all associated data. 
                          This action cannot be undone.
                        </p>
                        <p className="mt-2">
                          <strong>Note:</strong> If this user has jobs or other records associated with their account, 
                          deletion will fail. In that case, consider <strong>deactivating</strong> the user instead.
                        </p>
                      </div>
                      <div className="mt-4 flex space-x-3">
                        <button
                          type="button"
                          onClick={handleDeleteUser}
                          disabled={isLoading}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                        >
                          {isLoading ? 'Deleting...' : 'Yes, Delete Forever'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowDeleteConfirmation(false)}
                          className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
}
